import { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Heart, Upload, X, Edit2, Check, ImageIcon, Tag, Layers,
  Hash, Globe, ChevronDown, DollarSign, Calendar, CheckCircle,
  Trash2, RotateCcw, Search, MessageSquare,
} from 'lucide-react';
import {
  useWishlistItems, useCreateWishlistItem, useUpdateWishlistItem,
  useDeleteWishlistItem, useMarkWishlistFound, useReactivateWishlistItem,
} from '@/hooks/useApi';
import { formatDate } from '@/utils';
import toast from 'react-hot-toast';

// ─── Types ───────────────────────────────────────────────────
type PriceMode = 'none' | 'max' | 'range';
type ConditionMin = '' | 'NM' | 'LP' | 'MP' | 'HP';

interface WishlistItem {
  id: string;
  card_name: string;
  card_image_url?: string;
  set_name?: string;
  card_number?: string;
  language?: string;
  condition_min?: string;
  price_mode: string;
  price_min?: number;
  price_max?: number;
  is_active: boolean;
  created_at: string;
  fulfilled_at?: string;
}

// ─── Helpers ─────────────────────────────────────────────────
function priceBadge(item: WishlistItem) {
  if (item.price_mode === 'max' && item.price_max != null)
    return `Up to $${Number(item.price_max).toFixed(2)}`;
  if (item.price_mode === 'range' && item.price_min != null && item.price_max != null)
    return `$${Number(item.price_min).toFixed(2)} – $${Number(item.price_max).toFixed(2)}`;
  if (item.price_mode === 'min' && item.price_min != null)
    return `$${Number(item.price_min).toFixed(2)}+`;
  return 'Any price';
}

const CONDITION_LABELS: Record<string, string> = {
  NM: 'NM only',
  LP: 'LP or better',
  MP: 'MP or better',
  HP: 'HP or better',
};

// ─── Add-to-Wishlist Form ─────────────────────────────────────
function AddWishlistForm({ onAdded }: { onAdded: (item: WishlistItem) => void }) {
  const { mutateAsync: createItem, isLoading } = useCreateWishlistItem();

  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [cardName, setCardName] = useState('');
  const [setName, setSetName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [language, setLanguage] = useState('');
  const [conditionMin, setConditionMin] = useState<ConditionMin>('');
  const [priceMode, setPriceMode] = useState<PriceMode>('none');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = (f: File) => {
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(f.type)) {
      toast.error('Only JPG, PNG, or WebP images are accepted');
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target?.result as string);
    reader.readAsDataURL(f);
  };

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFile(dropped);
  }, []);

  const resetForm = () => {
    setFile(null);
    setPreview(null);
    setCardName('');
    setSetName('');
    setCardNumber('');
    setLanguage('');
    setConditionMin('');
    setPriceMode('none');
    setPriceMin('');
    setPriceMax('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cardName.trim()) { toast.error('Card name is required'); return; }

    const fd = new FormData();
    fd.append('card_name', cardName.trim());
    if (file) fd.append('card_image', file);
    if (setName.trim())    fd.append('set_name', setName.trim());
    if (cardNumber.trim()) fd.append('card_number', cardNumber.trim());
    if (language.trim())   fd.append('language', language.trim());
    if (conditionMin)      fd.append('condition_min', conditionMin);
    fd.append('price_mode', priceMode);
    if (priceMode !== 'none') {
      if (priceMode === 'max' || priceMode === 'range') fd.append('price_max', priceMax);
      if (priceMode === 'range') fd.append('price_min', priceMin);
    }

    const newItem = await createItem(fd);
    toast.success('Added to wishlist!');
    onAdded(newItem);
    setSuccess(true);
    resetForm();
    setTimeout(() => setSuccess(false), 3000);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white dark:bg-zinc-900 rounded-2xl shadow-sm border border-gray-200 dark:border-zinc-800 p-6 mb-8"
    >
      <h2 className="text-lg font-bold text-gray-800 dark:text-zinc-200 mb-5 flex items-center gap-2">
        <Heart className="h-5 w-5 text-pink-500" />
        Add a card to your wishlist
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6">
        {/* Image upload */}
        <div className="flex flex-col items-center gap-2">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`relative w-[110px] h-[154px] rounded-xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-colors overflow-hidden
              ${dragOver ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20' : 'border-gray-200 dark:border-zinc-800 hover:border-brand-400 bg-white dark:bg-zinc-900'}`}
          >
            {preview ? (
              <>
                <img src={preview} alt="preview" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setFile(null); setPreview(null); }}
                  className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-0.5 hover:bg-black/70"
                >
                  <X className="h-3 w-3" />
                </button>
              </>
            ) : (
              <>
                <Upload className="h-6 w-6 text-gray-500 dark:text-zinc-500 mb-1" />
                <p className="text-xs text-gray-500 dark:text-zinc-500 text-center px-2 leading-tight">
                  Drop card image here
                </p>
              </>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
          />
          <p className="text-xs text-gray-500 dark:text-zinc-500">JPG, PNG, WebP</p>
        </div>

        {/* Fields */}
        <div className="space-y-4">
          {/* Card name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">
              Card name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-zinc-500" />
              <input
                type="text"
                value={cardName}
                onChange={(e) => setCardName(e.target.value)}
                placeholder="e.g. Charizard"
                className="w-full pl-9 pr-4 py-2 border border-gray-200 dark:border-zinc-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
              />
            </div>
          </div>

          {/* Optional fields */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-gray-500 dark:text-zinc-500 mb-1">
                Set name <span className="text-gray-500 dark:text-zinc-500 font-normal">(optional)</span>
              </label>
              <div className="relative">
                <Layers className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500 dark:text-zinc-500" />
                <input
                  type="text"
                  value={setName}
                  onChange={(e) => setSetName(e.target.value)}
                  placeholder="Base Set"
                  className="w-full pl-8 pr-3 py-2 border border-gray-200 dark:border-zinc-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-zinc-500 mb-1">
                Card number <span className="text-gray-500 dark:text-zinc-500 font-normal">(optional)</span>
              </label>
              <div className="relative">
                <Hash className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500 dark:text-zinc-500" />
                <input
                  type="text"
                  value={cardNumber}
                  onChange={(e) => setCardNumber(e.target.value)}
                  placeholder="4/102"
                  className="w-full pl-8 pr-3 py-2 border border-gray-200 dark:border-zinc-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-zinc-500 mb-1">
                Language <span className="text-gray-500 dark:text-zinc-500 font-normal">(optional)</span>
              </label>
              <div className="relative">
                <Globe className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500 dark:text-zinc-500" />
                <input
                  type="text"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  placeholder="English"
                  className="w-full pl-8 pr-3 py-2 border border-gray-200 dark:border-zinc-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                />
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-zinc-500 -mt-2">Optional fields help find exact matches</p>

          {/* Condition minimum */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">
              Minimum condition
            </label>
            <div className="relative w-48">
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-zinc-500 pointer-events-none" />
              <select
                value={conditionMin}
                onChange={(e) => setConditionMin(e.target.value as ConditionMin)}
                className="w-full appearance-none pl-3 pr-8 py-2 border border-gray-200 dark:border-zinc-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-zinc-900"
              >
                <option value="">Any condition</option>
                <option value="HP">HP or better</option>
                <option value="MP">MP or better</option>
                <option value="LP">LP or better</option>
                <option value="NM">NM only</option>
              </select>
            </div>
          </div>

          {/* Price preference */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-2">
              Price preference
            </label>
            <div className="flex flex-wrap gap-4">
              {(
                [
                  { value: 'none', label: 'No price limit' },
                  { value: 'max', label: "Maximum I'll pay" },
                  { value: 'range', label: 'Price range' },
                ] as const
              ).map(({ value, label }) => (
                <label key={value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="price_mode"
                    value={value}
                    checked={priceMode === value}
                    onChange={() => { setPriceMode(value); setPriceMin(''); setPriceMax(''); }}
                    className="accent-brand-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-zinc-300">{label}</span>
                </label>
              ))}
            </div>

            {priceMode === 'max' && (
              <div className="mt-3 flex items-center gap-2">
                <div className="relative w-36">
                  <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-zinc-500" />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={priceMax}
                    onChange={(e) => setPriceMax(e.target.value)}
                    placeholder="0.00"
                    className="w-full pl-8 pr-3 py-2 border border-gray-200 dark:border-zinc-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                  />
                </div>
                <span className="text-sm text-gray-500 dark:text-zinc-500">maximum</span>
              </div>
            )}

            {priceMode === 'range' && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <div className="relative w-32">
                  <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-zinc-500" />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={priceMin}
                    onChange={(e) => setPriceMin(e.target.value)}
                    placeholder="Min"
                    className="w-full pl-8 pr-3 py-2 border border-gray-200 dark:border-zinc-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                  />
                </div>
                <span className="text-gray-500 dark:text-zinc-500">–</span>
                <div className="relative w-32">
                  <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-zinc-500" />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={priceMax}
                    onChange={(e) => setPriceMax(e.target.value)}
                    placeholder="Max"
                    className="w-full pl-8 pr-3 py-2 border border-gray-200 dark:border-zinc-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary flex items-center gap-2 disabled:opacity-60"
        >
          {isLoading ? (
            <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
          ) : success ? (
            <Check className="h-4 w-4" />
          ) : (
            <Heart className="h-4 w-4" />
          )}
          {success ? 'Added!' : 'Add to wishlist'}
        </button>
        {success && (
          <span className="text-sm text-green-600 font-medium">
            Added! We'll notify you when it's listed.
          </span>
        )}
      </div>
    </form>
  );
}

// ─── Edit Price Form (inline) ─────────────────────────────────
function EditPriceForm({
  item,
  onDone,
}: {
  item: WishlistItem;
  onDone: () => void;
}) {
  const { mutateAsync: updateItem, isLoading } = useUpdateWishlistItem();
  const [priceMode, setPriceMode] = useState<PriceMode>(
    (item.price_mode as PriceMode) || 'none'
  );
  const [priceMin, setPriceMin] = useState(item.price_min?.toString() ?? '');
  const [priceMax, setPriceMax] = useState(item.price_max?.toString() ?? '');
  const [conditionMin, setConditionMin] = useState<ConditionMin>(
    (item.condition_min as ConditionMin) ?? ''
  );

  const handleSave = async () => {
    await updateItem({
      id: item.id,
      data: {
        condition_min: conditionMin || null,
        price_mode: priceMode,
        price_min: priceMode === 'range' && priceMin ? parseFloat(priceMin) : null,
        price_max: (priceMode === 'max' || priceMode === 'range') && priceMax
          ? parseFloat(priceMax)
          : null,
      },
    });
    onDone();
  };

  return (
    <div className="mt-3 p-3 bg-white dark:bg-zinc-900 rounded-lg border border-gray-200 dark:border-zinc-800 space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500 dark:text-zinc-500 w-20 flex-shrink-0">Condition</span>
        <select
          value={conditionMin}
          onChange={(e) => setConditionMin(e.target.value as ConditionMin)}
          className="text-xs border border-gray-200 dark:border-zinc-800 rounded px-2 py-1 bg-white dark:bg-zinc-900 focus:outline-none focus:ring-1 focus:ring-brand-400"
        >
          <option value="">Any</option>
          <option value="HP">HP or better</option>
          <option value="MP">MP or better</option>
          <option value="LP">LP or better</option>
          <option value="NM">NM only</option>
        </select>
      </div>

      <div className="flex flex-wrap gap-3">
        {(['none', 'max', 'range'] as const).map((mode) => (
          <label key={mode} className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="radio"
              name={`pm-${item.id}`}
              value={mode}
              checked={priceMode === mode}
              onChange={() => { setPriceMode(mode); setPriceMin(''); setPriceMax(''); }}
              className="accent-brand-600"
            />
            <span className="text-xs text-gray-700 dark:text-zinc-300">
              {mode === 'none' ? 'Any price' : mode === 'max' ? 'Max price' : 'Range'}
            </span>
          </label>
        ))}
      </div>

      {priceMode === 'max' && (
        <div className="relative w-28">
          <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-500 dark:text-zinc-500" />
          <input
            type="number" min="0" step="0.01"
            value={priceMax} onChange={(e) => setPriceMax(e.target.value)}
            placeholder="0.00"
            className="w-full pl-6 pr-2 py-1 text-xs border border-gray-200 dark:border-zinc-800 rounded focus:outline-none focus:ring-1 focus:ring-brand-400"
          />
        </div>
      )}

      {priceMode === 'range' && (
        <div className="flex items-center gap-1.5">
          <div className="relative w-24">
            <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-500 dark:text-zinc-500" />
            <input
              type="number" min="0" step="0.01"
              value={priceMin} onChange={(e) => setPriceMin(e.target.value)}
              placeholder="Min"
              className="w-full pl-6 pr-2 py-1 text-xs border border-gray-200 dark:border-zinc-800 rounded focus:outline-none focus:ring-1 focus:ring-brand-400"
            />
          </div>
          <span className="text-gray-500 dark:text-zinc-500 text-xs">–</span>
          <div className="relative w-24">
            <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-500 dark:text-zinc-500" />
            <input
              type="number" min="0" step="0.01"
              value={priceMax} onChange={(e) => setPriceMax(e.target.value)}
              placeholder="Max"
              className="w-full pl-6 pr-2 py-1 text-xs border border-gray-200 dark:border-zinc-800 rounded focus:outline-none focus:ring-1 focus:ring-brand-400"
            />
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={isLoading}
          className="text-xs bg-brand-600 text-white rounded px-3 py-1 hover:bg-brand-700 disabled:opacity-60"
        >
          {isLoading ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="text-xs text-gray-500 dark:text-zinc-500 hover:text-gray-800 dark:text-zinc-200 px-2 py-1"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Wishlist Item Card ───────────────────────────────────────
function WishlistItemCard({ item }: { item: WishlistItem }) {
  const { mutate: deleteItem, isLoading: isDeleting } = useDeleteWishlistItem();
  const { mutate: markFound, isLoading: isMarkingFound } = useMarkWishlistFound();
  const { mutate: reactivate, isLoading: isReactivating } = useReactivateWishlistItem();
  const [editing, setEditing] = useState(false);
  const [localItem, setLocalItem] = useState(item);

  useEffect(() => setLocalItem(item), [item]);

  const isFulfilled = !localItem.is_active;
  const exploreUrl = `/explore?q=${encodeURIComponent(localItem.card_name)}`;

  return (
    <div
      className={`bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 shadow-sm overflow-hidden transition-opacity ${
        isFulfilled ? 'opacity-60' : ''
      }`}
    >
      <div className="flex gap-4 p-4">
        {/* Thumbnail 80×110 — clickable link to explore */}
        <Link
          to={exploreUrl}
          className="flex-shrink-0 w-[80px] h-[110px] rounded-lg bg-gradient-to-br from-zinc-800 to-gray-200 overflow-hidden group relative"
          title={`Search listings for "${localItem.card_name}"`}
        >
          {localItem.card_image_url ? (
            <img
              src={localItem.card_image_url}
              alt={localItem.card_name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="h-8 w-8 text-gray-400 dark:text-zinc-500" />
            </div>
          )}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
            <Search className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </Link>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <Link
              to={exploreUrl}
              className="font-bold text-gray-900 dark:text-zinc-100 hover:text-brand-600 dark:text-brand-400 truncate transition-colors"
              title={`Search listings for "${localItem.card_name}"`}
            >
              {localItem.card_name}
            </Link>
            <div className="flex items-center gap-1 flex-shrink-0">
              {!isFulfilled && (
                <button
                  onClick={() => setEditing((v) => !v)}
                  title="Edit price / condition"
                  className="p-1.5 text-gray-500 dark:text-zinc-500 hover:text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20 dark:bg-brand-900/20 rounded-lg transition-colors"
                >
                  <Edit2 className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                onClick={() => deleteItem(localItem.id)}
                disabled={isDeleting}
                title="Permanently delete"
                className="p-1.5 text-gray-500 dark:text-zinc-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 dark:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
            {localItem.set_name && (
              <span className="text-xs text-gray-500 dark:text-zinc-500">{localItem.set_name}</span>
            )}
            {localItem.card_number && (
              <span className="text-xs text-gray-500 dark:text-zinc-500">#{localItem.card_number}</span>
            )}
            {localItem.language && (
              <span className="text-xs text-gray-500 dark:text-zinc-500">{localItem.language}</span>
            )}
          </div>

          <div className="flex flex-wrap gap-2 mt-2">
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300">
              <DollarSign className="h-3 w-3" />
              {priceBadge(localItem)}
            </span>
            {localItem.condition_min && (
              <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300">
                {CONDITION_LABELS[localItem.condition_min] ?? localItem.condition_min}
              </span>
            )}
            {isFulfilled && (
              <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300">
                <CheckCircle className="h-3 w-3" /> Found
              </span>
            )}
          </div>

          <div className="flex items-center gap-1 mt-2 text-xs text-gray-500 dark:text-zinc-500">
            <Calendar className="h-3 w-3" />
            Added {formatDate(localItem.created_at, 'MMM d, yyyy')}
          </div>

          <div className="flex items-center gap-3 mt-2">
            {isFulfilled ? (
              <button
                onClick={() => reactivate(localItem.id)}
                disabled={isReactivating}
                className="text-xs text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:text-brand-300 hover:underline font-medium disabled:opacity-50 flex items-center gap-1"
              >
                <RotateCcw className="h-3 w-3" />
                {isReactivating ? 'Reactivating…' : 'Undo (still looking)'}
              </button>
            ) : (
              <button
                onClick={() => markFound(localItem.id)}
                disabled={isMarkingFound}
                className="text-xs text-green-600 hover:text-green-700 dark:text-green-300 hover:underline font-medium disabled:opacity-50 flex items-center gap-1"
              >
                <CheckCircle className="h-3 w-3" />
                {isMarkingFound ? 'Updating…' : 'Mark as found'}
              </button>
            )}
            <Link
              to={exploreUrl}
              className="text-xs text-gray-500 dark:text-zinc-500 hover:text-brand-600 dark:text-brand-400 hover:underline flex items-center gap-1"
            >
              <Search className="h-3 w-3" />
              Browse listings
            </Link>
            <Link
              to={`/forum?search=${encodeURIComponent(localItem.card_name)}`}
              className="text-xs text-gray-500 dark:text-zinc-500 hover:text-brand-600 dark:text-brand-400 hover:underline flex items-center gap-1"
            >
              <MessageSquare className="h-3 w-3" />
              Forum
            </Link>
          </div>
        </div>
      </div>

      {editing && !isFulfilled && (
        <div className="px-4 pb-4">
          <EditPriceForm item={localItem} onDone={() => setEditing(false)} />
        </div>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────
export default function WishlistPage() {
  const { data: serverItems = [], isLoading } = useWishlistItems();
  const [localItems, setLocalItems] = useState<WishlistItem[]>([]);

  useEffect(() => {
    setLocalItems(serverItems as WishlistItem[]);
  }, [serverItems]);

  const handleAdded = (newItem: WishlistItem) => {
    setLocalItems((prev) => [newItem, ...prev]);
  };

  const activeItems    = localItems.filter((i) => i.is_active);
  const fulfilledItems = localItems.filter((i) => !i.is_active);

  return (
    <div className="bg-white dark:bg-zinc-900 min-h-screen">
      <div className="bg-gradient-to-r from-pink-500 to-brand-600 text-white py-12">
        <div className="container-custom">
          <div className="flex items-center gap-3 mb-2">
            <Heart className="h-8 w-8 fill-white" />
            <h1 className="text-4xl font-display font-bold">Wishlist</h1>
          </div>
          <p className="text-pink-100">
            Cards you're hunting for — upload a photo and get notified when they're listed
          </p>
        </div>
      </div>

      <div className="container-custom py-8">
        <AddWishlistForm onAdded={handleAdded} />

        {isLoading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="skeleton h-36 rounded-xl" />
            ))}
          </div>
        ) : activeItems.length === 0 && fulfilledItems.length === 0 ? (
          <div className="text-center py-20">
            <Heart className="h-16 w-16 mx-auto text-gray-200 mb-4" />
            <h3 className="text-xl font-bold text-gray-700 dark:text-zinc-300 mb-2">Your wishlist is empty</h3>
            <p className="text-gray-500 dark:text-zinc-500 max-w-sm mx-auto">
              Upload a card you're looking for and we'll notify you when it's listed.
            </p>
          </div>
        ) : (
          <>
            {activeItems.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-sm font-semibold text-gray-500 dark:text-zinc-500 uppercase tracking-wider">
                  Active ({activeItems.length})
                </h2>
                {activeItems.map((item) => (
                  <WishlistItemCard key={item.id} item={item} />
                ))}
              </div>
            )}

            {fulfilledItems.length > 0 && (
              <div className="mt-8 space-y-4">
                <h2 className="text-sm font-semibold text-gray-500 dark:text-zinc-500 uppercase tracking-wider">
                  Found / Fulfilled ({fulfilledItems.length})
                </h2>
                {fulfilledItems.map((item) => (
                  <WishlistItemCard key={item.id} item={item} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
